// AGC: Permission Management Admin Panel
// AGC: Two tabs: (1) Role Permission Matrix, (2) User Role Assignment
// AGC: Uses unified RBAC system from types/rbac.ts

import React, { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { useTranslation } from '../../hooks/useTranslation'
import { getBackendUrl } from '../../utils/backendUrl'
import toast from 'react-hot-toast'
import {
  ROLE_PERMISSIONS,
  ROLE_INFO,
  type FarmRole,
  type FarmPermission,
} from '../../types/rbac'

// AGC: Permission features grouped by category for the matrix grid
const PERMISSION_GROUPS: {
  group: string
  groupKey: string
  icon: string
  permissions: { key: keyof FarmPermission; labelKey: string; fallback: string }[]
}[] = [
  {
    group: 'Campos',
    groupKey: 'admin.permGroup.fields',
    icon: '🗺️',
    permissions: [
      { key: 'canCreateField', labelKey: 'admin.perm.canCreateField', fallback: 'Criar campos' },
      { key: 'canEditField', labelKey: 'admin.perm.canEditField', fallback: 'Editar campos' },
      { key: 'canDeleteField', labelKey: 'admin.perm.canDeleteField', fallback: 'Excluir campos' },
      { key: 'canArchiveField', labelKey: 'admin.perm.canArchiveField', fallback: 'Arquivar campos' },
    ],
  },
  {
    group: 'Tarefas',
    groupKey: 'admin.permGroup.tasks',
    icon: '📋',
    permissions: [
      { key: 'canCreateTask', labelKey: 'admin.perm.canCreateTask', fallback: 'Criar tarefas' },
      { key: 'canEditTask', labelKey: 'admin.perm.canEditTask', fallback: 'Editar tarefas' },
      { key: 'canDeleteTask', labelKey: 'admin.perm.canDeleteTask', fallback: 'Excluir tarefas' },
      { key: 'canAssignTask', labelKey: 'admin.perm.canAssignTask', fallback: 'Atribuir tarefas' },
      { key: 'canUpdateTaskStatus', labelKey: 'admin.perm.canUpdateTaskStatus', fallback: 'Atualizar status' },
    ],
  },
  {
    group: 'Maquinário',
    groupKey: 'admin.permGroup.machines',
    icon: '🚜',
    permissions: [
      { key: 'canCreateMachine', labelKey: 'admin.perm.canCreateMachine', fallback: 'Criar máquinas' },
      { key: 'canEditMachine', labelKey: 'admin.perm.canEditMachine', fallback: 'Editar máquinas' },
      { key: 'canDeleteMachine', labelKey: 'admin.perm.canDeleteMachine', fallback: 'Excluir máquinas' },
      { key: 'canAssignMachine', labelKey: 'admin.perm.canAssignMachine', fallback: 'Atribuir máquinas' },
      { key: 'canViewMachineCosts', labelKey: 'admin.perm.canViewMachineCosts', fallback: 'Ver custos' },
    ],
  },
  {
    group: 'Inventário',
    groupKey: 'admin.permGroup.inventory',
    icon: '📦',
    permissions: [
      { key: 'canConsumeInventory', labelKey: 'admin.perm.canConsumeInventory', fallback: 'Consumir insumos' },
      { key: 'canManageInventory', labelKey: 'admin.perm.canManageInventory', fallback: 'Gerenciar estoque' },
    ],
  },
  {
    group: 'Equipe',
    groupKey: 'admin.permGroup.team',
    icon: '👥',
    permissions: [
      { key: 'canViewTeam', labelKey: 'admin.perm.canViewTeam', fallback: 'Ver equipe' },
      { key: 'canInviteUser', labelKey: 'admin.perm.canInviteUser', fallback: 'Convidar membros' },
      { key: 'canRemoveUser', labelKey: 'admin.perm.canRemoveUser', fallback: 'Remover membros' },
      { key: 'canChangeRoles', labelKey: 'admin.perm.canChangeRoles', fallback: 'Alterar perfis' },
    ],
  },
  {
    group: 'Financeiro',
    groupKey: 'admin.permGroup.financial',
    icon: '💰',
    permissions: [
      { key: 'canViewFinancial', labelKey: 'admin.perm.canViewFinancial', fallback: 'Ver financeiro' },
      { key: 'canViewProjectedValue', labelKey: 'admin.perm.canViewProjectedValue', fallback: 'Ver valor projetado' },
      { key: 'canEditFinancial', labelKey: 'admin.perm.canEditFinancial', fallback: 'Editar financeiro' },
    ],
  },
  {
    group: 'Admin',
    groupKey: 'admin.permGroup.admin',
    icon: '⚙️',
    permissions: [
      { key: 'canAccessAdmin', labelKey: 'admin.perm.canAccessAdmin', fallback: 'Painel admin' },
    ],
  },
]

// AGC: Roles to display in the matrix (owner is locked, not editable)
const DISPLAY_ROLES: FarmRole[] = ['owner', 'manager', 'agronomist', 'financial', 'worker', 'viewer']

// AGC: Sub-user from Firebase
interface SubUser {
  uid: string
  displayName: string | null
  email: string | null
  role: string
  subtype: string | null
  farmRole?: FarmRole
  isActive: boolean
  lastLoginAt: string | null
  createdAt: string
}

/**
 * AGC: PermissionManagement - Admin panel component for managing permissions
 * AGC: Tab 1: View/edit role permission matrix (RBAC-based)
 * AGC: Tab 2: Assign/change roles for sub-users
 */
export const PermissionManagement: React.FC = () => {
  const { user, getAuthToken } = useAuth()
  const { t } = useTranslation()

  // AGC: Active tab state
  const [activeTab, setActiveTab] = useState<'matrix' | 'users'>('matrix')

  // AGC: Sub-users list
  const [subUsers, setSubUsers] = useState<SubUser[]>([])
  const [loadingUsers, setLoadingUsers] = useState(false)

  // AGC: Custom permission overrides keyed by FarmRole (stored in Firestore adminConfig/permissions)
  const [customOverrides, setCustomOverrides] = useState<Record<string, Record<string, boolean>>>({})
  const [savingOverrides, setSavingOverrides] = useState(false)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)

  // AGC: Editing user role
  const [editingUserId, setEditingUserId] = useState<string | null>(null)
  const [editFarmRole, setEditFarmRole] = useState<FarmRole>('worker')

  // AGC: Load sub-users from Firebase
  const loadSubUsers = useCallback(async () => {
    if (!user?.uid) return
    setLoadingUsers(true)
    try {
      const backendUrl = getBackendUrl()
      const token = await getAuthToken()
      const response = await fetch(`${backendUrl}/api/v1/auth/my-users`, {
        headers: { 'Authorization': `Bearer ${token}` },
      })
      if (response.ok) {
        const data = await response.json()
        setSubUsers(data.users || [])
      } else {
        console.error('AGC: Error loading sub-users:', await response.text())
      }
    } catch (err) {
      console.error('AGC: Error loading sub-users:', err)
    }
    setLoadingUsers(false)
  }, [user?.uid, getAuthToken])

  // AGC: Load custom permission overrides from Firestore
  const loadCustomOverrides = useCallback(async () => {
    try {
      const backendUrl = getBackendUrl()
      const token = await getAuthToken()
      const response = await fetch(`${backendUrl}/api/v1/admin/config/permissions`, {
        headers: { 'Authorization': `Bearer ${token}` },
      })
      if (response.ok) {
        const data = await response.json()
        setCustomOverrides(data.overrides || {})
      }
    } catch (err) {
      console.warn('AGC: Could not load custom permission overrides:', err)
    }
  }, [getAuthToken])

  useEffect(() => {
    loadSubUsers()
    loadCustomOverrides()
  }, [loadSubUsers, loadCustomOverrides])

  // AGC: Save custom permission overrides to Firestore
  const saveCustomOverrides = async () => {
    setSavingOverrides(true)
    try {
      const backendUrl = getBackendUrl()
      const token = await getAuthToken()
      const response = await fetch(`${backendUrl}/api/v1/admin/config/permissions`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ overrides: customOverrides }),
      })
      if (response.ok) {
        toast.success(t('admin.permissionsSaved', 'Permissões salvas com sucesso!'))
        setHasUnsavedChanges(false)
      } else {
        toast.error(t('admin.permissionsSaveError', 'Erro ao salvar permissões'))
      }
    } catch (err) {
      console.error('AGC: Error saving permission overrides:', err)
      toast.error(t('admin.permissionsSaveError', 'Erro ao salvar permissões'))
    }
    setSavingOverrides(false)
  }

  // AGC: Reset all overrides for a single role (removes that role's key from overrides)
  const resetRoleOverrides = (role: FarmRole) => {
    if (role === 'owner') return
    setCustomOverrides(prev => {
      if (!prev[role]) return prev
      const updated = { ...prev }
      delete updated[role]
      return updated
    })
    setHasUnsavedChanges(true)
  }

  // AGC: Reset ALL overrides for ALL roles (after confirm)
  const resetAllOverrides = () => {
    if (!window.confirm(t('admin.confirmResetAll', 'Restaurar TODAS as permissões aos padrões? Esta ação não pode ser desfeita (clique em Salvar para aplicar).'))) return
    setCustomOverrides({})
    setHasUnsavedChanges(true)
  }

  // AGC: Toggle a permission override for a role
  const togglePermission = (role: FarmRole, permKey: keyof FarmPermission) => {
    // AGC: Don't allow changing owner permissions
    if (role === 'owner') return

    setCustomOverrides(prev => {
      const updated = { ...prev }
      if (!updated[role]) updated[role] = {}

      const defaultValue = ROLE_PERMISSIONS[role][permKey]

      // AGC: Toggle: if override exists, remove it (revert to default). Otherwise, set opposite of default
      if (updated[role][permKey] !== undefined) {
        delete updated[role][permKey]
        if (Object.keys(updated[role]).length === 0) delete updated[role]
      } else {
        updated[role] = { ...updated[role], [permKey]: !defaultValue }
      }

      return updated
    })
    setHasUnsavedChanges(true)
  }

  // AGC: Get effective permission value (default + custom override)
  const getEffectivePermission = (role: FarmRole, permKey: keyof FarmPermission): boolean => {
    // AGC: Check for custom override first
    if (customOverrides[role] && customOverrides[role][permKey] !== undefined) {
      return customOverrides[role][permKey]
    }
    return ROLE_PERMISSIONS[role][permKey]
  }

  // AGC: Check if a permission has been customized
  const isCustomized = (role: FarmRole, permKey: keyof FarmPermission): boolean => {
    return customOverrides[role]?.[permKey] !== undefined
  }

  // AGC: Update user farm role
  const updateUserRole = async (userId: string, newFarmRole: FarmRole) => {
    try {
      const backendUrl = getBackendUrl()
      const token = await getAuthToken()
      const response = await fetch(`${backendUrl}/api/v1/auth/user/${userId}/role`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ farmRole: newFarmRole }),
      })
      if (response.ok) {
        toast.success(t('admin.roleUpdated', 'Perfil atualizado com sucesso!'))
        setEditingUserId(null)
        loadSubUsers()
      } else {
        const errData = await response.json()
        toast.error(errData.message || t('admin.roleUpdateError', 'Erro ao atualizar perfil'))
      }
    } catch (err) {
      console.error('AGC: Error updating user role:', err)
      toast.error(t('admin.roleUpdateError', 'Erro ao atualizar perfil'))
    }
  }

  // AGC: Map legacy sub-user role to FarmRole for display
  const getUserFarmRole = (su: SubUser): FarmRole => {
    if (su.farmRole) return su.farmRole
    // AGC: Legacy mapping
    if (su.role === 'admin' || su.role === 'manager') return 'owner'
    if (su.role === 'submanager') return 'manager'
    if (su.role === 'user' && su.subtype === 'financeiro') return 'financial'
    if (su.role === 'user' && su.subtype === 'tecnico') return 'agronomist'
    return 'worker'
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-2" style={{ color: '#111827' }}>
          {ROLE_INFO.owner.icon} {t('admin.permissions', 'Permissões')}
        </h2>
        <p className="text-sm" style={{ color: '#374151' }}>
          {t('admin.permissionsDescription', 'Gerencie permissões por perfil e atribua perfis aos seus usuários.')}
        </p>
      </div>

      {/* AGC: Tab selector */}
      <div className="flex gap-2">
        <button
          onClick={() => setActiveTab('matrix')}
          className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
            activeTab === 'matrix'
              ? 'bg-emerald-500 text-white'
              : 'bg-slate-200 dark:bg-white/10 text-slate-900 dark:text-slate-100 hover:bg-slate-300 dark:hover:bg-white/[0.15]'
          }`}
        >
          {t('admin.rolePermissions', 'Permissões por Perfil')}
        </button>
        <button
          onClick={() => setActiveTab('users')}
          className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
            activeTab === 'users'
              ? 'bg-emerald-500 text-white'
              : 'bg-slate-200 dark:bg-white/10 text-slate-900 dark:text-slate-100 hover:bg-slate-300 dark:hover:bg-white/[0.15]'
          }`}
        >
          {t('admin.userRoles', 'Perfis de Usuários')}
        </button>
      </div>

      {/* AGC: Tab 1: Role Permission Matrix using RBAC */}
      {activeTab === 'matrix' && (
        <div className="bg-slate-100 dark:bg-white/[0.04] border border-slate-200 dark:border-white/5 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-200 dark:bg-white/10">
                  <th className="text-left px-3 py-2 font-semibold sticky left-0 bg-slate-200 dark:bg-slate-700/50 z-10 min-w-[180px]" style={{ color: '#111827' }}>
                    {t('admin.feature', 'Funcionalidade')}
                  </th>
                  {DISPLAY_ROLES.map(role => {
                    const hasRoleOverrides = !!customOverrides[role] && Object.keys(customOverrides[role]).length > 0
                    return (
                      <th key={role} className="text-center px-2 py-2 font-semibold min-w-[80px]" style={{ color: '#111827' }}>
                        <div className="text-xs">
                          <span className="block">{ROLE_INFO[role].icon}</span>
                          {ROLE_INFO[role].label}
                          {hasRoleOverrides && role !== 'owner' && (
                            <button
                              onClick={() => resetRoleOverrides(role)}
                              className="block mx-auto mt-1 text-[10px] text-blue-600 hover:text-blue-800 underline"
                              title={t('admin.resetRoleTooltip', 'Restaurar padrões deste perfil')}
                            >
                              {t('admin.resetRole', 'restaurar')}
                            </button>
                          )}
                        </div>
                      </th>
                    )
                  })}
                </tr>
              </thead>
              <tbody>
                {PERMISSION_GROUPS.map(group => (
                  <React.Fragment key={group.group}>
                    {/* AGC: Group header row */}
                    <tr className="bg-slate-200 dark:bg-white/10">
                      <td
                        colSpan={DISPLAY_ROLES.length + 1}
                        className="px-3 py-1.5 text-xs font-bold uppercase tracking-wide sticky left-0 bg-slate-200 dark:bg-slate-700/50"
                        style={{ color: '#374151' }}
                      >
                        {group.icon} {t(group.groupKey, group.group)}
                      </td>
                    </tr>
                    {group.permissions.map(perm => (
                      <tr key={perm.key} className="border-t border-slate-200 dark:border-white/5 hover:bg-slate-50 dark:hover:bg-white/[0.02]">
                        <td className="px-3 py-1.5 text-slate-800 dark:text-slate-200 sticky left-0 bg-slate-100 dark:bg-slate-800/50 z-10 text-xs">
                          {t(perm.labelKey, perm.fallback)}
                        </td>
                        {DISPLAY_ROLES.map(role => {
                          const isOn = getEffectivePermission(role, perm.key)
                          const isCustom = isCustomized(role, perm.key)
                          const isOwner = role === 'owner'
                          return (
                            <td key={role} className="text-center px-2 py-1.5">
                              <button
                                onClick={() => !isOwner && togglePermission(role, perm.key)}
                                disabled={isOwner}
                                className={`w-7 h-7 rounded-md flex items-center justify-center mx-auto transition-all text-xs ${
                                  isOwner
                                    ? 'bg-success-50 dark:bg-emerald-500/15 text-brand-primary dark:text-emerald-400 cursor-not-allowed opacity-50'
                                    : isOn
                                      ? isCustom
                                        ? 'bg-blue-200 text-blue-800 border-2 border-blue-400 hover:bg-blue-300'
                                        : 'bg-emerald-200 text-emerald-800 border-2 border-emerald-500 hover:bg-emerald-300'
                                      : isCustom
                                        ? 'bg-red-200 text-red-800 border-2 border-red-400 hover:bg-red-300'
                                        : 'bg-slate-200 dark:bg-white/10 text-slate-700 dark:text-slate-300 border-2 border-slate-400 dark:border-white/20 hover:bg-slate-300 dark:hover:bg-white/[0.15]'
                                }`}
                                title={isCustom
                                  ? t('admin.permCustom', 'Personalizado (clique para reverter)')
                                  : t('admin.permDefault', 'Padrão (clique para alterar)')}
                              >
                                {isOn ? '✓' : '✕'}
                              </button>
                            </td>
                          )
                        })}
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* AGC: Legend and Save button */}
          <div className="px-4 py-3 border-t border-slate-200 dark:border-white/5 flex items-center justify-between bg-slate-50 dark:bg-white/[0.02]">
            <div className="flex gap-4 text-xs text-slate-700 dark:text-slate-300">
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded bg-emerald-200 inline-block"></span> {t('admin.defaultPermission', 'Padrão')}
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded bg-blue-200 border border-blue-400 inline-block"></span> {t('admin.customEnabled', 'Habilitado (custom)')}
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded bg-red-200 border border-red-400 inline-block"></span> {t('admin.customDisabled', 'Desabilitado (custom)')}
              </span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={resetAllOverrides}
                disabled={Object.keys(customOverrides).length === 0}
                className="px-3 py-1.5 bg-slate-200 dark:bg-white/10 hover:bg-slate-300 dark:hover:bg-white/[0.15] rounded text-slate-800 dark:text-slate-200 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title={t('admin.resetAllTooltip', 'Restaurar todas as permissões aos padrões')}
              >
                {t('admin.resetAll', 'Restaurar tudo')}
              </button>
              <button
                onClick={saveCustomOverrides}
                disabled={savingOverrides || !hasUnsavedChanges}
                className={`px-4 py-1.5 rounded text-white text-sm font-medium transition-colors ${
                  hasUnsavedChanges
                    ? 'bg-emerald-500 hover:bg-emerald-600'
                    : 'bg-slate-400 dark:bg-slate-600 cursor-not-allowed'
                } disabled:opacity-50`}
              >
              {savingOverrides ? '...' : t('common.save', 'Salvar')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AGC: Tab 2: User Role Assignment */}
      {activeTab === 'users' && (
        <div className="space-y-3">
          {loadingUsers ? (
            <div className="text-center py-8 text-slate-600 dark:text-slate-300">
              {t('common.loading', 'Carregando...')}
            </div>
          ) : subUsers.length === 0 ? (
            <div className="text-center py-8 text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-white/[0.04] rounded-lg">
              <p className="text-lg mb-2">{ROLE_INFO.viewer.icon}</p>
              <p>{t('admin.noSubUsers', 'Nenhum sub-usuário cadastrado.')}</p>
              <p className="text-xs mt-1">{t('admin.createUsersHint', 'Crie usuários na seção "Usuários" do menu.')}</p>
            </div>
          ) : (
            subUsers.map(su => {
              const farmRole = getUserFarmRole(su)
              const roleInfo = ROLE_INFO[farmRole]
              const isEditing = editingUserId === su.uid

              return (
                <div key={su.uid} className="bg-slate-100 dark:bg-white/[0.04] border border-slate-200 dark:border-white/5 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${su.isActive ? 'bg-emerald-500' : 'bg-red-500'}`}></span>
                        <span className="text-slate-900 dark:text-slate-100 font-medium">{su.displayName || su.email || 'Usuário'}</span>
                      </div>
                      <div className="text-slate-600 dark:text-slate-300 text-xs mt-1">
                        {su.email && <span>{su.email} · </span>}
                        <span className="text-brand-primary dark:text-emerald-400">{roleInfo.icon} {roleInfo.label}</span>
                        {su.lastLoginAt && (
                          <span> · {t('admin.lastAccess', 'Último acesso')}: {new Date(su.lastLoginAt).toLocaleDateString('pt-BR')}</span>
                        )}
                      </div>
                    </div>

                    {!isEditing ? (
                      <button
                        onClick={() => {
                          setEditingUserId(su.uid)
                          setEditFarmRole(farmRole)
                        }}
                        className="px-3 py-1.5 bg-slate-200 dark:bg-white/10 hover:bg-slate-300 dark:hover:bg-white/[0.15] rounded text-slate-800 dark:text-slate-200 text-xs transition-colors"
                      >
                        {t('admin.changeRole', 'Alterar Perfil')}
                      </button>
                    ) : (
                      <div className="flex items-center gap-2">
                        <select
                          value={editFarmRole}
                          onChange={(e) => setEditFarmRole(e.target.value as FarmRole)}
                          className="px-2 py-1 bg-slate-100 dark:bg-white/[0.04] border border-slate-200 dark:border-white/10 rounded text-slate-900 dark:text-slate-100 text-xs"
                        >
                          {/* AGC: Owner can't be assigned — only non-owner roles shown */}
                          <option value="manager">{ROLE_INFO.manager.icon} {ROLE_INFO.manager.label}</option>
                          <option value="agronomist">{ROLE_INFO.agronomist.icon} {ROLE_INFO.agronomist.label}</option>
                          <option value="financial">{ROLE_INFO.financial.icon} {ROLE_INFO.financial.label}</option>
                          <option value="worker">{ROLE_INFO.worker.icon} {ROLE_INFO.worker.label}</option>
                          <option value="viewer">{ROLE_INFO.viewer.icon} {ROLE_INFO.viewer.label}</option>
                        </select>
                        <button
                          onClick={() => updateUserRole(su.uid, editFarmRole)}
                          className="px-2 py-1 bg-emerald-500 hover:bg-emerald-600 rounded text-white text-xs"
                        >
                          ✓
                        </button>
                        <button
                          onClick={() => setEditingUserId(null)}
                          className="px-2 py-1 bg-slate-200 dark:bg-white/10 hover:bg-slate-300 dark:hover:bg-white/[0.15] rounded text-slate-800 dark:text-slate-200 text-xs"
                        >
                          ✕
                        </button>
                      </div>
                    )}
                  </div>

                  {/* AGC: Show key permissions summary for the user's role */}
                  <div className="mt-2 flex flex-wrap gap-1">
                    {PERMISSION_GROUPS.slice(0, 4).map(group => {
                      const allEnabled = group.permissions.every(p => getEffectivePermission(farmRole, p.key))
                      const someEnabled = group.permissions.some(p => getEffectivePermission(farmRole, p.key))
                      return (
                        <span
                          key={group.group}
                          className={`text-xs px-1.5 py-0.5 rounded ${
                            allEnabled
                              ? 'bg-success-50 dark:bg-emerald-500/15 text-brand-primary dark:text-emerald-400'
                              : someEnabled
                                ? 'bg-warning-50 dark:bg-yellow-500/15 text-warning-700 dark:text-yellow-400'
                                : 'bg-slate-200 dark:bg-white/10 text-slate-500 dark:text-slate-400'
                          }`}
                          title={t(group.groupKey, group.group)}
                        >
                          {group.icon}
                        </span>
                      )
                    })}
                  </div>
                </div>
              )
            })
          )}

          <button
            onClick={loadSubUsers}
            disabled={loadingUsers}
            className="w-full px-4 py-2 bg-slate-200 dark:bg-white/10 hover:bg-slate-300 dark:hover:bg-white/[0.15] rounded-lg text-slate-800 dark:text-slate-200 text-sm transition-colors disabled:opacity-50"
          >
            {t('common.refresh', 'Atualizar')}
          </button>
        </div>
      )}
    </div>
  )
}
